import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, LinkSimple, MapPin, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1518611540400-6b85a0704342?w=1000&q=70";

export default function PersonDetail() {
  const { id } = useParams();
  const { t, tc } = useLang();
  const [person, setPerson] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  useEffect(() => {
    api
      .get(`/people/${id}`)
      .then((r) => setPerson(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6" data-testid="detail-notfound">
        <div className="font-display font-black uppercase text-5xl">404</div>
        <Link to="/" className="font-mono text-xs uppercase tracking-[0.25em] text-primary underline">
          {t("detail.back")}
        </Link>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="flex min-h-screen items-center justify-center font-mono text-sm uppercase tracking-[0.3em] text-muted-foreground">
        {t("detail.loading")}
      </div>
    );
  }

  const date = person.created_at ? new Date(person.created_at).toISOString().slice(0, 19).replace("T", " ") : "";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2" data-testid="person-detail">
      {/* LEFT: pinned portrait */}
      <div ref={ref} className="relative border-b border-border md:border-b-0 md:border-r">
        <div className="md:sticky md:top-0 md:h-screen overflow-hidden bg-secondary">
          <motion.img
            style={{ y: imgY }}
            src={person.photo || PLACEHOLDER}
            alt={person.full_name}
            onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
            className="h-[70vh] md:h-[112%] w-full object-cover"
            data-testid="detail-portrait"
          />
          <div className="pointer-events-none absolute left-5 top-24 h-6 w-6 border-l-2 border-t-2 border-accent" />
          <div className="pointer-events-none absolute right-5 bottom-5 h-6 w-6 border-r-2 border-b-2 border-accent" />
          <div className="absolute left-5 bottom-5 font-mono text-[10px] uppercase tracking-[0.25em] text-background bg-foreground px-3 py-1.5">
            {t("detail.record")} {person.id.slice(0, 8)}
          </div>
        </div>
      </div>

      {/* RIGHT: dossier */}
      <div className="px-6 md:px-12 py-28">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary hover:text-accent transition-colors mb-12"
          data-testid="detail-back"
        >
          <ArrowLeft size={14} weight="bold" /> {t("detail.back")}
        </Link>

        <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground mb-4">
          <span className="border border-border px-2 py-1 text-foreground">{tc(person.category)}</span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="font-display font-black uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl text-foreground"
        >
          {person.full_name}
        </motion.h1>

        {/* Data table */}
        <div className="mt-12 border-t border-border font-mono text-sm">
          {[
            { k: t("detail.category"), v: tc(person.category) },
            { k: t("detail.region"), v: person.region || "—", icon: <MapPin size={14} /> },
            { k: t("detail.severity"), v: `${person.severity} / 5`, icon: <Warning size={14} /> },
            { k: t("detail.dateAdded"), v: date },
          ].map((row) => (
            <div key={row.k} className="flex items-center justify-between border-b border-border py-4">
              <span className="uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
                {row.icon} {row.k}
              </span>
              <span className="text-foreground uppercase tracking-wider">{row.v}</span>
            </div>
          ))}
        </div>

        {/* Description */}
        <div className="mt-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
            {t("detail.reason")}
          </div>
          <p className="font-mono text-base md:text-lg leading-relaxed text-foreground max-w-2xl">
            {person.description || t("detail.noDescription")}
          </p>
        </div>

        {/* Sources */}
        {person.sources && person.sources.length > 0 && (
          <div className="mt-12">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
              {t("detail.sources")}
            </div>
            <ul className="space-y-3">
              {person.sources.map((s, i) => (
                <li key={i}>
                  <a
                    href={s}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-mono text-sm text-primary hover:text-accent transition-colors break-all"
                    data-testid={`source-link-${i}`}
                  >
                    <LinkSimple size={14} weight="bold" /> {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
